package com.zq.webproject.ui.profile

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import com.zq.webproject.utils.UrlLauncher
import com.zq.webproject.getPlatform
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * 我的页面ViewModel
 * 管理用户余额和升级功能
 */
class ProfileViewModel {
 val repository = Repository()
 private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

 // 用户余额
 var balance by mutableStateOf<Double?>(null)
  private set

 // 用户角色
 var userRole by mutableStateOf<String?>(null)
  private set

 // 用户收益
 var earnings by mutableStateOf<Double?>(null)
  private set

 // 加载状态
 var isLoading by mutableStateOf(false)
  private set

 // 错误信息
 var errorMessage by mutableStateOf<String?>(null)
  private set

 // 成功信息
 var successMessage by mutableStateOf<String?>(null)
  private set
  
 // 支付宝二维码URL
 var alipayQrcodeUrl by mutableStateOf<String?>(null)
  private set
  
 // 支付宝订单号（用于轮询）
 var alipayOrderNo by mutableStateOf<String?>(null)
  private set

 /**
  * 加载用户余额
  */
 fun loadBalance() {
  scope.launch {
   val token = TokenManager.getToken()
   if (token.isNullOrEmpty()) {
    println("ProfileViewModel - Token为空，无法加载余额") // 日志输出
    errorMessage = "未登录，无法获取余额"
    return@launch
   }

   println("ProfileViewModel - 开始加载用户余额") // 日志输出
   isLoading = true
   errorMessage = null

   val result = repository.getUserBalance(token)

   if (result.isFailure) {
    val error = "获取余额失败: ${result.exceptionOrNull()?.message}"
    println("ProfileViewModel - $error") // 日志输出
    errorMessage = error
    isLoading = false
    return@launch
   }

   val response = result.getOrNull()
   if (response?.code == 200 && response.data != null) {
    balance = response.data.balance
    println("ProfileViewModel - 余额加载成功: ${response.data.balance}") // 日志输出
   } else {
    errorMessage = "获取余额失败: ${response?.message ?: "未知错误"}"
    println("ProfileViewModel - $errorMessage") // 日志输出
   }

   isLoading = false
  }
 }

 /**
  * 加载用户角色
  */
 fun loadUserRole() {
  scope.launch {
   val token = TokenManager.getToken()
   if (token.isNullOrEmpty()) {
    println("ProfileViewModel - Token为空，无法加载角色") // 日志输出
    return@launch
   }

   println("ProfileViewModel - 开始加载用户角色") // 日志输出

   val result = repository.getUserRole(token)

   if (result.isFailure) {
    println("ProfileViewModel - 获取角色失败: ${result.exceptionOrNull()?.message}") // 日志输出
    return@launch
   }

   val response = result.getOrNull()
   if (response?.code == 200 && response.data != null) {
    userRole = response.data.role
    println("ProfileViewModel - 角色加载成功: ${response.data.role}") // 日志输出
   } else {
    println("ProfileViewModel - 获取角色失败: ${response?.message}") // 日志输出
   }
  }
 }

 /**
  * 加载用户收益
  */
 fun loadEarnings() {
  scope.launch {
   val token = TokenManager.getToken()
   if (token.isNullOrEmpty()) {
    println("ProfileViewModel - Token为空，无法加载收益") // 日志输出
    return@launch
   }

   println("ProfileViewModel - 开始加载用户收益") // 日志输出

   val result = repository.getUserEarnings(token)

   if (result.isFailure) {
    println("ProfileViewModel - 获取收益失败: ${result.exceptionOrNull()?.message}") // 日志输出
    return@launch
   }

   val response = result.getOrNull()
   if (response?.code == 200 && response.data != null) {
    earnings = response.data.totalEarnings
    println("ProfileViewModel - 收益加载成功: $earnings") // 日志输出
   } else {
    println("ProfileViewModel - 获取收益失败: ${response?.message}") // 日志输出
   }
  }
 }

 /**
  * 升级为加盟商（仅支持余额支付）
  */
 fun upgradeToAffiliate() {
  scope.launch {
   val token = TokenManager.getToken()
   if (token.isNullOrEmpty()) {
    println("ProfileViewModel - Token为空，无法升级")
    errorMessage = "未登录，无法升级"
    return@launch
   }

   println("ProfileViewModel - 开始升级为加盟商（余额支付）")
   isLoading = true
   errorMessage = null
   successMessage = null

   // 调用升级接口（仅支持余额支付）
   val result = repository.upgradeToAffiliate(token, "balance")

   if (result.isFailure) {
    val error = "升级失败: ${result.exceptionOrNull()?.message}"
    println("ProfileViewModel - $error")
    errorMessage = error
    isLoading = false
    return@launch
   }

   val response = result.getOrNull()
   
   // 余额支付处理
   if (response?.code == 200) {
    successMessage = response.message
    println("ProfileViewModel - 升级成功: ${response.message}")
    // 升级成功后重新加载余额和角色
    loadBalance()
    loadUserRole()
   } else {
    errorMessage = response?.message ?: "升级失败"
    println("ProfileViewModel - 升级失败: ${response?.message}")
   }

   isLoading = false
  }
 }
 
 /**
  * 清除错误信息
  */
 fun clearError() {
  errorMessage = null
 }

 /**
  * 清除成功信息
  */
 fun clearSuccess() {
  successMessage = null
 }
}